/**
 * App.jsx - Main Application Component
 * Handles authentication, user roles, and routing
 */

import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, loginRequest, sharePointConfig } from './config/authConfig';
import Dashboard from './pages/Dashboard';
import LandingPage from './pages/LandingPage';
import RulesPage from './pages/RulesPage';
import NotificationsPage from './pages/NotificationsPage';
import Login from './pages/Login';
import Layout from './components/Layout';
import useStore from './store/useStore';

import './App.css';

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

function AppContent() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const { setUser, setAccessToken, initialize, setUserRole, userRole } = useStore();
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [initError, setInitError] = useState(null);

  useEffect(() => {
    const setupAuth = async () => {
      // Don't run if:
      // - Not authenticated
      // - Already initialized
      // - Currently initializing
      // - User is blocked
      if (!isAuthenticated || hasInitialized || isInitializing || isBlocked) {
        return;
      }

      if (accounts.length === 0) {
        return;
      }

      setIsInitializing(true);
      setInitError(null);
      const account = accounts[0];

      try {
        console.log('[App] Starting authentication setup...');
        setUser(account);

        // FIRST: Acquire access token
        let tokenResponse;
        try {
          tokenResponse = await instance.acquireTokenSilent({
            ...loginRequest,
            account: account,
          });
        } catch (error) {
          console.error('[App] Silent token acquisition failed, trying interactive:', error);
          // Fallback to interactive
          tokenResponse = await instance.acquireTokenPopup(loginRequest);
        }

        console.log('[App] ✅ Access token acquired successfully');
        
        // SECOND: Set token in store (this sets it in sharePointService)
        setAccessToken(tokenResponse.accessToken);
        
        // THIRD: Get user role from SharePoint
        console.log('[App] 🔍 Fetching user role for:', account.username);
        const sharePointService = (await import('./services/sharePointService')).default;
        const role = await sharePointService.getUserRole(account.username);
        console.log('[App] ✅ User role retrieved:', role);
        
        // FOURTH: Check if user is blocked
        if (role === 'Blocked') {
          console.log('[App] 🚫 User is blocked');
          setIsBlocked(true);
          setUserRole('Blocked');
          setIsInitializing(false);
          setHasInitialized(true); // Mark as initialized to prevent re-runs
          return; // STOP HERE - don't initialize store
        }
        
        // FIFTH: Set user role
        setUserRole(role);
        setIsBlocked(false);
        
        // SIXTH: Now initialize store (will use the token)
        console.log('[App] 🔄 Initializing store with data...');
        await initialize(sharePointConfig.siteUrl);
        console.log('[App] ✅ Store initialized successfully');
        
        // Mark as initialized
        setHasInitialized(true);
        
      } catch (error) {
        console.error('[App] ❌ Authentication setup failed:', error);
        setInitError(error.message || 'Failed to initialize application');
      } finally {
        setIsInitializing(false);
      }
    };

    setupAuth();
  }, [isAuthenticated, accounts.length, hasInitialized, isInitializing, isBlocked]); // Removed instance, setUser, etc. to prevent unnecessary reruns

  // Handle logout - reset all states
  useEffect(() => {
    if (!isAuthenticated && (hasInitialized || isBlocked)) {
      console.log('[App] User logged out, resetting state');
      setHasInitialized(false);
      setIsBlocked(false);
      setIsInitializing(false);
      setInitError(null);
    }
  }, [isAuthenticated, hasInitialized, isBlocked]);

  // Not authenticated - show login
  if (!isAuthenticated) {
    return <Login />;
  }

  // Show error if initialization failed
  if (initError && !isBlocked) {
    return (
      <div className="app-error">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h1>Initialization Error</h1>
          <p>{initError}</p>
          <button 
            className="btn-retry"
            onClick={() => {
              setInitError(null);
              setHasInitialized(false);
              setIsInitializing(false);
            }}
          >
            Retry
          </button>
          <button 
            className="btn-logout"
            onClick={() => instance.logoutPopup()}
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  // User is blocked - show access denied (BEFORE loading check)
  if (isBlocked) {
    return (
      <div className="app-blocked">
        <div className="blocked-container">
          <div className="blocked-icon">🚫</div>
          <h1>Access Denied</h1>
          <p>Your access to the Production Intelligence System has been revoked.</p>
          <p>Please contact your system administrator for assistance.</p>
          {/* <div className="blocked-details">
            <p><strong>User:</strong> {accounts[0]?.username || 'Unknown'}</p>
            <p><strong>Name:</strong> {accounts[0]?.name || 'Unknown'}</p>
            <p><strong>Status:</strong> Blocked</p>
          </div> */}
          <button 
            className="btn-logout"
            onClick={() => {
              console.log('[App] Logging out blocked user');
              instance.logoutPopup();
            }}
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  // Initializing - show loading
  if (isInitializing || (!hasInitialized && !isBlocked)) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading Production Intelligence...</p>
        <p className="loading-subtitle">Authenticating and loading your data...</p>
      </div>
    );
  }

  // Authenticated and initialized - show app
  return (
    <Router>
      <Layout>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/rules" element={<RulesPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </Router>
  );
}

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <AppContent />
    </MsalProvider>
  );
}

export default App;